import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@material-ui/core';
import { Skeleton } from '@mui/material';

const LazyImage = ({
    src,
    alt,
    width,
    height,
    className,
    style,
    placeholder = null,
    fallback = null,
    onLoad,
    onError,
    priority = false, // For above-the-fold images
    sizes = null, // For responsive images
    // Enhanced SEO props
    title = null,
    caption = null,
    credit = null,
    license = null,
    creator = null,
    uploadDate = null,
    encodingFormat = null,
    // Enhanced data attributes
    'data-connector-name': dataConnectorName = null,
    'data-connector-status': dataConnectorStatus = null,
    'data-connector-type': dataConnectorType = null,
    'data-image-role': dataImageRole = null,
    'data-image-category': dataImageCategory = null,
    ...props
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(priority);
    const [hasError, setHasError] = useState(false);
    const [imageSrc, setImageSrc] = useState(priority ? src : null);
    const imgRef = useRef(null);
    const observerRef = useRef(null);

    useEffect(() => {
        // If priority is true, load immediately
        if (priority) {
            setImageSrc(src);
            return;
        }

        // Set up intersection observer for lazy loading
        if ('IntersectionObserver' in window) {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            setIsInView(true);
                            setImageSrc(src);
                            observerRef.current.unobserve(entry.target);
                        }
                    });
                },
                {
                    rootMargin: '50px 0px', // Start loading 50px before the image comes into view
                    threshold: 0.01
                }
            );

            if (imgRef.current) {
                observerRef.current.observe(imgRef.current);
            }
        } else {
            // Fallback for browsers that don't support IntersectionObserver
            setIsInView(true);
            setImageSrc(src);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [src, priority]);

    const handleLoad = () => {
        setIsLoaded(true);
        if (onLoad) onLoad();
    };

    const handleError = () => {
        setHasError(true);
        if (fallback) {
            setImageSrc(fallback);
        }
        if (onError) onError();
    };

    const getFallbackAltText = () => {
        if (!src || typeof src !== 'string') return 'Image';
        const cleanSrc = src.split('?')[0];
        const fileName = cleanSrc.split('/').pop() || '';
        const baseName = fileName.replace(/\.[^/.]+$/, '');
        if (!baseName) return 'Image';
        return baseName.replace(/[-_]+/g, ' ').trim() || 'Image';
    };

    // Enhanced alt text validation
    const validateAltText = (altText) => {
        if (!altText || altText.trim() === '') {
            return getFallbackAltText();
        }
        return altText;
    };

    // Enhanced image attributes for SEO
    const getImageAttributes = () => {
        const attributes = {
            src: imageSrc,
            alt: validateAltText(alt),
            width: width,
            height: height,
            className: className,
            style: style,
            onLoad: handleLoad,
            onError: handleError,
            loading: priority ? 'eager' : 'lazy',
            sizes: sizes,
            // Enhanced SEO attributes
            title: title || alt,
            // Structured data attributes
            'data-image-loaded': isLoaded,
            'data-image-error': hasError,
            'data-image-priority': priority,
            'data-image-src': src,
            // Connector-specific attributes
            ...(dataConnectorName && { 'data-connector-name': dataConnectorName }),
            ...(dataConnectorStatus && { 'data-connector-status': dataConnectorStatus }),
            ...(dataConnectorType && { 'data-connector-type': dataConnectorType }),
            ...(dataImageRole && { 'data-image-role': dataImageRole }),
            ...(dataImageCategory && { 'data-image-category': dataImageCategory }),
            // Additional SEO attributes
            ...(caption && { 'data-caption': caption }),
            ...(credit && { 'data-credit': credit }),
            ...(license && { 'data-license': license }),
            ...(creator && { 'data-creator': creator }),
            ...(uploadDate && { 'data-upload-date': uploadDate }),
            ...(encodingFormat && { 'data-encoding-format': encodingFormat }),
            ...props
        };

        return attributes;
    };

    // Show skeleton while loading
    if (!isLoaded && isInView) {
        return (
            <Box ref={imgRef} style={{ position: 'relative' }}>
                <Skeleton
                    variant="rectangular"
                    width={width || '100%'}
                    height={height || 200}
                    style={{ borderRadius: '8px' }}
                />
                <img
                    {...getImageAttributes()}
                    alt={validateAltText(alt)}
                    style={{
                        ...style,
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                    }}
                />
            </Box>
        );
    }

    // Show placeholder while not in view
    if (!isInView) {
        return (
            <Box ref={imgRef}>
                {placeholder || (
                    <Skeleton
                        variant="rectangular"
                        width={width || '100%'}
                        height={height || 200}
                        style={{ borderRadius: '8px' }}
                    />
                )}
            </Box>
        );
    }

    // Show loaded image
    return (
        <img
            ref={imgRef}
            {...getImageAttributes()}
            alt={validateAltText(alt)}
        />
    );
};

export default LazyImage;
